import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { htmlEncode } from '../../../utils/escape.utils';
import {
  WEB_COMPONENT_MAINTENANCE_WINDOW_MANUALLY_APPROVED_PATCHES_SELECTOR,
  WEB_COMPONENT_MAINTENANCE_WINDOW_PATCH_MANAGEMENT_VENDOR_PRODUCT_SELECTOR,
  WEB_COMPONENT_RELAY_PATCH_CACHING_SERVERS,
} from '../../../webcomponents.constants';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { MaintenanceWindowPatchSpecificsFormData, Schedulers } from '../types';
import { MaintenanceWindowSchedulerWebComponent } from '../../../maintenanceWindows/maintenanceWindowScheduler';
import { MaintenanceWindowSimpleSchedulerWebComponent } from '../../../maintenanceWindows/maintenanceWindowSimpleScheduler';
import { MaintenanceWindowsConstants } from '../../../maintenanceWindows/MaintenanceWindowsConstants';
import { CustomInclusiveDisjunctionValidator } from '../../../ui/gzForm/validators/custom-inclusive-disjunction.validator';
import { translateFn } from '../../../utils/translator.helper';
import { MaintenanceWindowManuallyApprovedPatchesSelectorWebComponent } from '../../../maintenanceWindows/maintenanceWindowManuallyApprovedPatchesSelector';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { GzCheckboxWebComponent } from '../../../ui/gzCheckbox';

export class MaintenanceWindowFormPatchSpecificsView extends BaseWebComponentView {
  public formGroup!: FormGroup;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.form-wrapper');

    // Patch checkboxes Area
    this.dom.$scanPatchesFormField = this.shadow.querySelector(
      '#scan-for-patches-form-field'
    ) as GzFormFieldWebComponent<GzCheckboxWebComponent>;
    this.dom.$applyPatchesFormField = this.shadow.querySelector('#apply-patches-form-field');
    this.dom.$applyPatchesSecurityFormField = this.shadow.querySelector('#apply-patches-security-form-field');
    this.dom.$applyPatchesNonSecurityFormField = this.shadow.querySelector('#apply-patches-non-security-form-field');
    this.dom.$applyPatchesManuallyApprovedFormField = this.shadow.querySelector(
      '#apply-patches-manually-approved-form-field'
    );
    this.dom.$manuallyApprovedPatchesInfoText = this.shadow.querySelector('#manually-approved-patches-info-text');
    this.dom.$manuallyApprovedPatchesSelector = this.shadow.querySelector(
      WEB_COMPONENT_MAINTENANCE_WINDOW_MANUALLY_APPROVED_PATCHES_SELECTOR
    ) as MaintenanceWindowManuallyApprovedPatchesSelectorWebComponent;
    this.dom.$operationTargetedLabeledContent = this.shadow.querySelector('#operation-targeted-labeled-content');
    this.dom.$patchScopeLabeledContent = this.shadow.querySelector('#patch-scope-labeled-content');
    this.dom.$patchOptionsContainer = this.shadow.querySelector('#patch-options-container');
    this.dom.$scanDependentContainer = this.shadow.querySelector('#scan-dependent-container');

    this.dom.$runOnSystemChangesFormField = this.shadow.querySelector('#run-on-system-changes-form-field');
    this.dom.$runOnSystemChangesText = this.shadow.querySelector('#runOnSystemChangesText');

    // Scheduler Options Area
    this.dom.schedulingOptions = {
      $mainContainer: this.shadow.querySelector('#schedulers-container'),
      $schedulingOptionsTitle: this.shadow.querySelector('#scheduling-options-title'),

      $useSameScheduleFormField: this.shadow.querySelector('#use-same-schedule-form-field'),
      $useFallbackScheduleFormField: this.shadow.querySelector('#use-fallback-schedule-form-field'),
      $fallbackScheduleNoticeWarning: this.shadow.querySelector('#fallback-schedule-notice-container'),
      $ifMissedRunAsSoonAsPossibleFormField: this.shadow.querySelector('#if-missed-run-as-soon-as-possible-form-field'),
    };
    this.dom.schedulers = {
      same: {
        $container: this.shadow.querySelector('#single-schedule-container'),
        $newScheduler: this.shadow.querySelector('#schedule-for-all'),
        $oldScheduler: this.shadow.querySelector('#schedule-for-all-simple'),
      },
      individual: {
        $container: this.shadow.querySelector('#individual-schedules-container'),
        scan: {
          $container: this.shadow.querySelector('#schedule-scanning-patches-container'),
          $newScheduler: this.shadow.querySelector('#schedule-scanning-patches'),
          $oldScheduler: this.shadow.querySelector('#schedule-scanning-patches-simple'),
        },
        applySecurity: {
          $container: this.shadow.querySelector('#schedule-applying-patches-security-container'),
          $newScheduler: this.shadow.querySelector('#schedule-applying-patches-security'),
          $oldScheduler: this.shadow.querySelector('#schedule-applying-patches-security-simple'),
        },
        applyNonSecurity: {
          $container: this.shadow.querySelector('#schedule-applying-patches-non-security-container'),
          $newScheduler: this.shadow.querySelector('#schedule-applying-patches-non-security'),
          $oldScheduler: this.shadow.querySelector('#schedule-applying-patches-non-security-simple'),
        },
      },
    };

    this.dom.rebootPreferences = {
      $container: this.shadow.querySelector('#reboot-preferences-container'),
      $rebootOptions: this.shadow.querySelector('#reboot-options'),
    };

    this.dom.additionalPatchSettings = {
      $container: this.shadow.querySelector('#additional-patch-settings-container'),
      $vendorProductPairsContainer: this.shadow.querySelector('#vendor-products-pairs-container'),
      $title: this.shadow.querySelector('#additional-patch-settings-title'),
      $specificRelaysTitle: this.shadow.querySelector('#specific-relays-title'),
      $specificRelaysDescription: this.shadow.querySelector('#specific-relays-description'),

      $useVendorWebsitesFallbackFormField: this.shadow.querySelector('#use-vendor-websites-fallback-form-field'),

      $specificVendorsProductsTitle: this.shadow.querySelector('#specific-vendors-products-title'),
      $specificVendorsProductsDescription: this.shadow.querySelector('#specific-vendors-products-description'),
      $specificVendorsProductsLabeledContent: this.shadow.querySelector('#vendors-products-to-be-labeled-content'),
      $specificVendorsProductsRadioGroupFormField: this.shadow.querySelector('#vendors-products-to-be-form-field'),
      $specificVendorsProductsRadioButtonFirst: this.shadow.querySelector('#vendors-products-to-be-options-first'),
      $specificVendorsProductsRadioButtonSecond: this.shadow.querySelector('#vendors-products-to-be-options-second'),
      $relayPatchCachingServersLabeledContent: this.shadow.querySelector(
        '#relay-patch-caching-servers-labeled-content'
      ),
      $relayPatchCachingServers: this.shadow.querySelector(WEB_COMPONENT_RELAY_PATCH_CACHING_SERVERS),
      $vendorsProductsSelectorLabeledContent: this.shadow.querySelector('#vendors-products-selector-labeled-content'),
      $vendorProductSelector: this.shadow.querySelector(
        WEB_COMPONENT_MAINTENANCE_WINDOW_PATCH_MANAGEMENT_VENDOR_PRODUCT_SELECTOR
      ),
    };

    this.injectDependenciesInChildren();
    this.setTranslatedStrings();
  }

  private injectDependenciesInChildren() {
    // manually approved patches selector
    if (this.dom.$manuallyApprovedPatchesSelector.injectDependencies) {
      this.dom.$manuallyApprovedPatchesSelector.injectDependencies(this.dependencies);
      this.dom.$manuallyApprovedPatchesSelector.start().catch(console.error);
    }

    // new schedulers
    if (this.dom.schedulers.same.$newScheduler.injectDependencies) {
      this.dom.schedulers.same.$newScheduler.injectDependencies(this.dependencies);
      this.dom.schedulers.same.$newScheduler.start().catch(console.error);
    }

    if (this.dom.schedulers.individual.scan.$newScheduler.injectDependencies) {
      this.dom.schedulers.individual.scan.$newScheduler.injectDependencies(this.dependencies);
      this.dom.schedulers.individual.scan.$newScheduler.start().catch(console.error);
    }

    if (this.dom.schedulers.individual.applySecurity.$newScheduler.injectDependencies) {
      this.dom.schedulers.individual.applySecurity.$newScheduler.injectDependencies(this.dependencies);
      this.dom.schedulers.individual.applySecurity.$newScheduler.start().catch(console.error);
    }

    if (this.dom.schedulers.individual.applyNonSecurity.$newScheduler.injectDependencies) {
      this.dom.schedulers.individual.applyNonSecurity.$newScheduler.injectDependencies(this.dependencies);
      this.dom.schedulers.individual.applyNonSecurity.$newScheduler.start().catch(console.error);
    }

    // old schedulers
    if (this.dom.schedulers.same.$oldScheduler.injectDependencies) {
      this.dom.schedulers.same.$oldScheduler.injectDependencies(this.dependencies);
      this.dom.schedulers.same.$oldScheduler.start().catch(console.error);
    }

    if (this.dom.schedulers.individual.scan.$oldScheduler.injectDependencies) {
      this.dom.schedulers.individual.scan.$oldScheduler.injectDependencies(this.dependencies);
      this.dom.schedulers.individual.scan.$oldScheduler.start().catch(console.error);
    }

    if (this.dom.schedulers.individual.applySecurity.$oldScheduler.injectDependencies) {
      this.dom.schedulers.individual.applySecurity.$oldScheduler.injectDependencies(this.dependencies);
      this.dom.schedulers.individual.applySecurity.$oldScheduler.start().catch(console.error);
    }

    if (this.dom.schedulers.individual.applyNonSecurity.$oldScheduler.injectDependencies) {
      this.dom.schedulers.individual.applyNonSecurity.$oldScheduler.injectDependencies(this.dependencies);
      this.dom.schedulers.individual.applyNonSecurity.$oldScheduler.start().catch(console.error);
    }

    if (this.dom.rebootPreferences.$rebootOptions.injectDependencies) {
      this.dom.rebootPreferences.$rebootOptions.injectDependencies(this.dependencies);
      this.dom.rebootPreferences.$rebootOptions.start().catch(console.error);
    }

    if (this.dom.additionalPatchSettings.$relayPatchCachingServers.injectDependencies) {
      this.dom.additionalPatchSettings.$relayPatchCachingServers.injectDependencies(this.dependencies);
      this.dom.additionalPatchSettings.$relayPatchCachingServers.start().catch(console.error);
    }

    if (this.dom.additionalPatchSettings.$vendorProductSelector.injectDependencies) {
      this.dom.additionalPatchSettings.$vendorProductSelector.injectDependencies(this.dependencies);
      this.dom.additionalPatchSettings.$vendorProductSelector.start().catch(console.error);
    }
  }

  setTranslatedStrings() {
    this.dom.$operationTargetedLabeledContent.label = htmlEncode(this.dependencies.translateFn('Targeted operations'));
    this.dom.$patchScopeLabeledContent.label = htmlEncode(this.dependencies.translateFn('Patch scope'));

    this.dom.$scanPatchesFormField.Input.label = htmlEncode(this.dependencies.translateFn('Scan for patches'));
    this.dom.$applyPatchesFormField.Input.label = htmlEncode(this.dependencies.translateFn('Apply patches'));
    this.dom.$applyPatchesSecurityFormField.Input.label = htmlEncode(this.dependencies.translateFn('Security'));
    this.dom.$applyPatchesNonSecurityFormField.Input.label = htmlEncode(this.dependencies.translateFn('Non-security'));
    this.dom.$applyPatchesManuallyApprovedFormField.Input.label = htmlEncode(translateFn('Manually approved'));
    this.dom.$manuallyApprovedPatchesInfoText.innerHTML = htmlEncode(
      translateFn(
        'Select to automatically install patches that otherwise require manual approval on endpoints, such as Windows feature updates. They install alongside security patches and ignore any existing vendor and product exclusion rules.'
      )
    );
    this.dom.$runOnSystemChangesFormField.Input.innerText = this.dependencies.translateFn(
      'Smart scan for patches when new applications are installed'
    );
    this.dom.$runOnSystemChangesText.innerHTML = htmlEncode(
      this.dependencies.translateFn(
        'When a new application is installed on the endpoint, the security agent automatically installs all discovered OS and application updates, regardless of any planned scan and installation tasks'
      )
    );

    this.setTranslatedStringsSchedulingOptions();
    this.setTranslatedStringsAdditionalPatchSettings();
    this.buildScheduleNoticeInfoBox();
  }

  setTranslatedStringsSchedulingOptions() {
    this.dom.schedulingOptions.$schedulingOptionsTitle.innerText = this.dependencies.translateFn('Scheduling options');
    this.dom.schedulingOptions.$useSameScheduleFormField.Input.innerText = this.dependencies.translateFn(
      'Use the same schedule for all targeted operations'
    );
    this.dom.schedulingOptions.$useFallbackScheduleFormField.Input.innerText = this.dependencies.translateFn(
      'Use fallback schedule compatible with Bitdefender Endpoint Security Tools for Windows version 7.3.2.x or older'
    );
    this.dom.schedulingOptions.$ifMissedRunAsSoonAsPossibleFormField.Input.innerText = this.dependencies.translateFn(
      'If missed, run as soon as possible'
    );

    // new schedulers
    this.dom.schedulers.same.$newScheduler.setTitle(this.dependencies.translateFn('Schedule for all operations'));
    this.dom.schedulers.individual.scan.$newScheduler.setTitle(
      htmlEncode(this.dependencies.translateFn('Schedule for patch scanning'))
    );
    this.dom.schedulers.individual.applySecurity.$newScheduler.setTitle(
      this.dependencies.translateFn('Schedule for applying patches - security')
    );
    this.dom.schedulers.individual.applyNonSecurity.$newScheduler.setTitle(
      this.dependencies.translateFn('Schedule for applying patches - non-security')
    );

    // old schedulers
    this.dom.schedulers.same.$oldScheduler.setTitle(this.dependencies.translateFn('Schedule for all operations'));
    this.dom.schedulers.individual.scan.$oldScheduler.setTitle(
      htmlEncode(this.dependencies.translateFn('Schedule for patch scanning'))
    );
    this.dom.schedulers.individual.applySecurity.$oldScheduler.setTitle(
      this.dependencies.translateFn('Schedule for applying patches - security')
    );
    this.dom.schedulers.individual.applyNonSecurity.$oldScheduler.setTitle(
      this.dependencies.translateFn('Schedule for applying patches - non-security')
    );
  }

  setTranslatedStringsAdditionalPatchSettings() {
    this.dom.additionalPatchSettings.$title.innerText = this.dependencies.translateFn('Additional patch settings');
    this.dom.additionalPatchSettings.$specificRelaysTitle.innerText = this.dependencies.translateFn('Relays');
    this.dom.additionalPatchSettings.$specificRelaysDescription.innerText =
      this.dependencies.translateFn('Define your patch download settings by adding specific relays.') +
      '\n' +
      translateFn("Patch caching servers provide patches only to Windows endpoints. Linux and macOS endpoints download patches directly from the vendors' websites.");
    this.dom.additionalPatchSettings.$useVendorWebsitesFallbackFormField.Input.innerText =
      this.dependencies.translateFn('Use vendor websites as fallback location to download patches');
    this.dom.additionalPatchSettings.$specificVendorsProductsTitle.innerText =
      this.dependencies.translateFn('Vendors and products');
    this.dom.additionalPatchSettings.$specificVendorsProductsDescription.innerText = this.dependencies.translateFn(
      'Specify which vendors and products to be included or excluded from being patched. The selected option applies to the entire list. If no vendor or product is specified, all installed products will be patched.'
    );
    this.dom.additionalPatchSettings.$specificVendorsProductsLabeledContent.label = htmlEncode(
      this.dependencies.translateFn('Vendors and products to be')
    );
    this.dom.additionalPatchSettings.$specificVendorsProductsRadioButtonFirst.label = htmlEncode(
      this.dependencies.translateFn('Included')
    );
    this.dom.additionalPatchSettings.$specificVendorsProductsRadioButtonSecond.label = htmlEncode(
      this.dependencies.translateFn('Excluded')
    );
    this.dom.additionalPatchSettings.$relayPatchCachingServersLabeledContent.label = htmlEncode(
      this.dependencies.translateFn('Relay with Patch Caching Server')
    );
    this.dom.additionalPatchSettings.$vendorsProductsSelectorLabeledContent.label = htmlEncode(
      this.dependencies.translateFn('Select vendors and products')
    );
  }

  private buildScheduleNoticeInfoBox(): void {
    this.dom.schedulingOptions.$fallbackScheduleNoticeWarning.innerHTML = `
    <span>
        ${htmlEncode(
          this.dependencies.translateFn(
            'The fallback schedule is compatible with Bitdefender Endpoint Security Tools for Windows up to version 7.3.2.x. To use extended scheduling capabilities, you must update the security agent to version 7.3.3.x or later.'
          )
        )}
    </span>`;
  }

  isCachingServersSet(): boolean {
    return this.dom.additionalPatchSettings.$relayPatchCachingServers.cachingServers.length > 0;
  }

  private createFormGroup() {
    // set validator CustomInclusiveDisjunctionValidator
    this.dom.additionalPatchSettings.$useVendorWebsitesFallbackFormField.Validators = [
      CustomInclusiveDisjunctionValidator(
        this.isCachingServersSet.bind(this),
        this.dependencies.translateFn(
          "You must define at least one Relay with Patch Caching Server or check the option 'Use fallback location'!"
        )
      ),
    ];
    // create FormGroup
    this.formGroup = new FormGroup({
      scanForPatches: this.dom.$scanPatchesFormField,
      applyPatches: this.dom.$applyPatchesFormField,
      patchScopeSecurity: this.dom.$applyPatchesSecurityFormField,
      patchScopeNonSecurity: this.dom.$applyPatchesNonSecurityFormField,
      patchScopeManuallyApproved: this.dom.$applyPatchesManuallyApprovedFormField,
      manuallyApprovedPatchIds: this.dom.$manuallyApprovedPatchesSelector.controller.view.getFormField(),
      schedulingOptions: new FormGroup({
        useSameSchedule: this.dom.schedulingOptions.$useSameScheduleFormField,
        useFallbackSchedule: this.dom.schedulingOptions.$useFallbackScheduleFormField,
        runAsSoonAsPossible: this.dom.schedulingOptions.$ifMissedRunAsSoonAsPossibleFormField,
        schedulerForAll: this.getScheduler(Schedulers.SAME).controller?.view.getFormGroup() || new FormGroup({}),
        schedulerScanningPatches:
          this.getScheduler(Schedulers.SCAN)?.controller?.view.getFormGroup() || new FormGroup({}),
        schedulerApplyingPatchesSecurity:
          this.getScheduler(Schedulers.APPLY_SECURITY).controller?.view.getFormGroup() || new FormGroup({}),
        schedulerApplyingPatchesNonSecurity:
          this.getScheduler(Schedulers.APPLY_NON_SECURITY).controller?.view.getFormGroup() || new FormGroup({}),
        fallbackSchedulers: new FormGroup({
          schedulerForAll:
            this.getScheduler(Schedulers.SAME, true).controller?.view.getFormGroup() || new FormGroup({}),
          schedulerScanningPatches:
            this.getScheduler(Schedulers.SCAN, true)?.controller?.view.getFormGroup() || new FormGroup({}),
          schedulerApplyingPatchesSecurity:
            this.getScheduler(Schedulers.APPLY_SECURITY, true).controller?.view.getFormGroup() || new FormGroup({}),
          schedulerApplyingPatchesNonSecurity:
            this.getScheduler(Schedulers.APPLY_NON_SECURITY, true).controller?.view.getFormGroup() || new FormGroup({}),
        }),
      }),
      runOnSystemChanges: this.dom.$runOnSystemChangesFormField,
      rebootOptions: this.dom.rebootPreferences.$rebootOptions.controller?.view.getFormGroup(),
      fallbackOnDirectDownload: this.dom.additionalPatchSettings.$useVendorWebsitesFallbackFormField,
      vendorProductsPairsType: this.dom.additionalPatchSettings.$specificVendorsProductsRadioGroupFormField,
    });
  }

  public getScheduler(
    type: Schedulers,
    isFallback = false
  ): MaintenanceWindowSchedulerWebComponent | MaintenanceWindowSimpleSchedulerWebComponent {
    switch (type) {
      case Schedulers.SAME:
        return isFallback ? this.dom.schedulers.same.$oldScheduler : this.dom.schedulers.same.$newScheduler;
      case Schedulers.SCAN:
        return isFallback
          ? this.dom.schedulers.individual.scan.$oldScheduler
          : this.dom.schedulers.individual.scan.$newScheduler;
      case Schedulers.APPLY_SECURITY:
        return isFallback
          ? this.dom.schedulers.individual.applySecurity.$oldScheduler
          : this.dom.schedulers.individual.applySecurity.$newScheduler;
      case Schedulers.APPLY_NON_SECURITY:
        return isFallback
          ? this.dom.schedulers.individual.applyNonSecurity.$oldScheduler
          : this.dom.schedulers.individual.applyNonSecurity.$newScheduler;
    }
  }

  public getFormGroup(): FormGroup {
    return this.formGroup;
  }

  buildSuccessView(data: { viewData: MaintenanceWindowPatchSpecificsFormData; disabled: boolean }) {
    this.createFormGroup();
    this.setFormData(data.viewData);
    if (data.disabled) {
      this.setDisabledInnerComponents(data.disabled);
    }
  }

  private setFormData(viewData: MaintenanceWindowPatchSpecificsFormData) {
    if (viewData?.isEditMode) {
      // set edit data
      const specifics = viewData.maintenanceWindowPatchSpecificsData;
      this.preventDefaultOnTargetedOperations(true);
      this.formGroup.value = {
        scanForPatches: specifics.scanForPatches,
        applyPatches: specifics.applyPatches,
        patchScopeSecurity: specifics.patchScopeSecurity,
        patchScopeNonSecurity: specifics.patchScopeNonSecurity,
        patchScopeManuallyApproved: specifics.patchScopeManuallyApproved,
        manuallyApprovedPatchIds: specifics.manuallyApprovedPatchIds,
        schedulingOptions: {
          useSameSchedule: specifics.schedulingOptions.useSameSchedule,
          useFallbackSchedule: specifics.schedulingOptions.useFallbackSchedule,
          runAsSoonAsPossible: specifics.schedulingOptions.runAsSoonAsPossible,
        },
        runOnSystemChanges: specifics.runOnSystemChanges,
        fallbackOnDirectDownload: specifics.fallbackOnDirectDownload,
        vendorProductsPairsType: specifics.vendorProductsPairsType,
      };
      this.preventDefaultOnTargetedOperations(false);

      // set values in manually approved patches selector
      this.dom.$manuallyApprovedPatchesSelector.selectedPatchIds = specifics.manuallyApprovedPatchIds;

      // set values in schedulers
      this.dom.schedulers.same.$newScheduler.viewData = specifics.schedulingOptions.schedulerForAll;
      this.dom.schedulers.individual.scan.$newScheduler.viewData = specifics.schedulingOptions.schedulerScanningPatches;
      this.dom.schedulers.individual.applySecurity.$newScheduler.viewData =
        specifics.schedulingOptions.schedulerApplyingPatchesSecurity;
      this.dom.schedulers.individual.applyNonSecurity.$newScheduler.viewData =
        specifics.schedulingOptions.schedulerApplyingPatchesNonSecurity;

      this.dom.schedulers.same.$oldScheduler.viewData = specifics.schedulingOptions.fallbackSchedulers?.schedulerForAll;
      this.dom.schedulers.individual.scan.$oldScheduler.viewData =
        specifics.schedulingOptions.fallbackSchedulers?.schedulerScanningPatches;
      this.dom.schedulers.individual.applySecurity.$oldScheduler.viewData =
        specifics.schedulingOptions.fallbackSchedulers?.schedulerApplyingPatchesSecurity;
      this.dom.schedulers.individual.applyNonSecurity.$oldScheduler.viewData =
        specifics.schedulingOptions.fallbackSchedulers?.schedulerApplyingPatchesNonSecurity;

      // set this.dom.rebootPreferences.$rebootOptions values
      if (specifics.rebootOptions !== undefined) {
        this.dom.rebootPreferences.$rebootOptions.viewData = specifics.rebootOptions;
      }
      // set this.dom.additionalPatchSettings.$relayPatchCachingServers values
      this.dom.additionalPatchSettings.$relayPatchCachingServers.cachingServers = specifics.cachingServers;
      this.dom.additionalPatchSettings.$relayPatchCachingServers.companyId =
        this.dependencies.getActiveCompany().companyId;
      // set this.dom.additionalPatchSettings.$vendorProductSelector values
      this.dom.additionalPatchSettings.$vendorProductSelector.vendorProductsPairs = specifics['vendorProductsPairs'];
    } else {
      this.setDefaultFormData();
    }
  }

  preventDefaultOnTargetedOperations(stopEmitEvents: boolean) {
    this.dom.$scanPatchesFormField.Input.stopEmitEvents = stopEmitEvents;
    this.dom.$applyPatchesFormField.Input.stopEmitEvents = stopEmitEvents;
    this.dom.$applyPatchesSecurityFormField.Input.stopEmitEvents = stopEmitEvents;
    this.dom.$applyPatchesNonSecurityFormField.Input.stopEmitEvents = stopEmitEvents;
    this.dom.$applyPatchesManuallyApprovedFormField.Input.stopEmitEvents = stopEmitEvents;
  }

  private setDefaultFormData() {
    this.preventDefaultOnTargetedOperations(true);
    this.formGroup.value = {
      scanForPatches: false,
      applyPatches: false,
      patchScopeSecurity: false,
      patchScopeNonSecurity: false,
      patchScopeManuallyApproved: false,
      manuallyApprovedPatchIds: [],
      schedulingOptions: {
        useSameSchedule: false,
        useFallbackSchedule: false,
        runAsSoonAsPossible: true,
      },
      runOnSystemChanges: true,
      fallbackOnDirectDownload: true,
      vendorProductsPairsType: '1',
    };
    this.preventDefaultOnTargetedOperations(false);

    // reset manually approved patches selector
    this.dom.$manuallyApprovedPatchesSelector.selectedPatchIds = [];

    // reset schedulers
    this.dom.schedulers.same.$newScheduler.viewData = MaintenanceWindowsConstants.scanNewScheduleDefaultValues;
    this.dom.schedulers.same.$oldScheduler.viewData = MaintenanceWindowsConstants.scanOldScheduleDefaultValues;
    this.dom.schedulers.individual.scan.$newScheduler.viewData =
      MaintenanceWindowsConstants.scanNewScheduleDefaultValues;
    this.dom.schedulers.individual.scan.$oldScheduler.viewData =
      MaintenanceWindowsConstants.scanOldScheduleDefaultValues;
    this.dom.schedulers.individual.applySecurity.$newScheduler.viewData =
      MaintenanceWindowsConstants.securityNewScheduleDefaultValues;
    this.dom.schedulers.individual.applySecurity.$oldScheduler.viewData =
      MaintenanceWindowsConstants.securityOldScheduleDefaultValues;
    this.dom.schedulers.individual.applyNonSecurity.$newScheduler.viewData =
      MaintenanceWindowsConstants.nonSecurityNewScheduleDefaultValues;
    this.dom.schedulers.individual.applyNonSecurity.$oldScheduler.viewData =
      MaintenanceWindowsConstants.nonSecurityOldScheduleDefaultValues;

    // reset this.dom.rebootPreferences.$rebootOptions,
    this.dom.rebootPreferences.$rebootOptions.viewData = MaintenanceWindowsConstants.rebootOptionsDefaultValues;
    // reset this.dom.additionalPatchSettings.$relayPatchCachingServers,
    this.dom.additionalPatchSettings.$relayPatchCachingServers.cachingServers = [];
    this.dom.additionalPatchSettings.$relayPatchCachingServers.companyId =
      this.dependencies.getActiveCompany().companyId;
    // reset this.dom.additionalPatchSettings.$vendorProductSelector,
    this.dom.additionalPatchSettings.$vendorProductSelector.vendorProductsPairs = [];
  }

  setDisabledInnerComponents(disable: boolean) {
    const disableableComponents = this.getDisableableComponents();
    disableableComponents.forEach(disableableComponent => {
      disableableComponent.disabled = disable;
    });
  }

  private getDisableableComponents() {
    return [
      this.dom.$scanPatchesFormField,
      this.dom.$applyPatchesFormField,
      this.dom.$applyPatchesSecurityFormField,
      this.dom.$applyPatchesNonSecurityFormField,
      this.dom.$applyPatchesManuallyApprovedFormField,
      this.dom.$manuallyApprovedPatchesSelector,
      this.dom.$runOnSystemChangesFormField,
      this.dom.schedulingOptions.$useSameScheduleFormField,
      this.dom.schedulingOptions.$useFallbackScheduleFormField,
      this.dom.schedulingOptions.$ifMissedRunAsSoonAsPossibleFormField,
      this.dom.schedulers.same.$newScheduler,
      this.dom.schedulers.same.$oldScheduler,
      this.dom.schedulers.individual.scan.$newScheduler,
      this.dom.schedulers.individual.scan.$oldScheduler,
      this.dom.schedulers.individual.applySecurity.$newScheduler,
      this.dom.schedulers.individual.applySecurity.$oldScheduler,
      this.dom.schedulers.individual.applyNonSecurity.$newScheduler,
      this.dom.schedulers.individual.applyNonSecurity.$oldScheduler,
      this.dom.rebootPreferences.$rebootOptions,
      this.dom.additionalPatchSettings.$useVendorWebsitesFallbackFormField,
      this.dom.additionalPatchSettings.$specificVendorsProductsRadioGroupFormField,
      this.dom.additionalPatchSettings.$relayPatchCachingServers,
      this.dom.additionalPatchSettings.$vendorProductSelector,
    ];
  }
}
